import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, onAuthStateChanged } from '../config/firebase';
import { PrivateProfileService } from '../services/privateProfileService';
import { mentorChatService, MentorChatService } from '../services/mentorChatService';
import { 
  MentorChatHistory, 
  PrivateProfile, 
  DailyRecommendations 
} from '../models/PrivateProfile';
import { loggingService } from '../services/loggingService';
import { chatMessageReceived } from '../utils/logging/actions';
import { ChatPhase1Schema, ChatPhase2Schema } from '../utils/mentorSchemas';
import { useSubscriptionContext } from './SubscriptionContext';

// Configuration constants
const CHAT_HISTORY_LIMIT = 5; // Number of interactions (10 messages total)
const FREE_MESSAGE_LIMIT = 5; // Number of free messages before requiring subscription

// Chat message type for UI
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  isStreaming?: boolean;
  recommendations?: {
    books: Array<{ bookId: string; reason: string; recommendedAt: number }>;
    insights: Array<{ insightId: string; reason: string; bookId?: string; recommendedAt: number }>;
  };
  profileUpdates?: {
    coreProfile?: {
      goals?: string[];
      challenges?: string[];
      interests?: string[];
      values?: string[];
      personalityTraits?: string[];
    };
    activeContext?: {
      currentFocus?: string[];
      urgentNeeds?: string[];
      emotionalState?: string;
      information?: string;
    };
    changesSummary?: string;
  };
}

interface MentorChatContextType {
  // State
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  profile: PrivateProfile | null;
  
  // Actions
  sendMessage: (message: string) => Promise<void>;
  loadChatHistory: () => Promise<void>;
  clearChat: () => void;
  handleQuickAction: (userMessage: string, mentorResponse: string) => void;
  
  // Streaming state
  currentStreamingMessage: string;
  streamingMessageId: string | null;
  
  // Error handling
  error: string | null;
  lastFailedMessage: string | null;
  retryLastMessage: () => Promise<void>;
  clearError: () => void;

  // Unread status
  hasUnreadMessages: boolean;
  markChatAsRead: () => Promise<void>;
}

const MentorChatContext = createContext<MentorChatContextType | undefined>(undefined);

export const useMentorChat = () => {
  const context = useContext(MentorChatContext);
  if (!context) {
    throw new Error('useMentorChat must be used within a MentorChatProvider');
  }
  return context;
};

interface MentorChatProviderProps {
  children: React.ReactNode;
}

export const MentorChatProvider: React.FC<MentorChatProviderProps> = ({ children }) => {
  const { hasAIAccess } = useSubscriptionContext();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [profile, setProfile] = useState<PrivateProfile | null>(null);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  // Calculate unread status whenever profile changes
  useEffect(() => {
    if (!profile) return;

    const lastReadTimestampStr = profile.userData?.['lastChatReadTimestamp'];
    
    // If never read, and it's the first time (or we want to show dot initially)
    if (!lastReadTimestampStr) {
      setHasUnreadMessages(true);
      return;
    }

    const lastReadTime = parseFloat(lastReadTimestampStr);
    const history = profile.chatHistory || [];

    if (history.length === 0) {
      // If user has read before but history is empty (cleared?), no unread messages
      setHasUnreadMessages(false);
      return;
    }

    // Check if any message is newer than last read time
    // Find max timestamp in history
    const maxTimestamp = Math.max(...history.map(m => m.timestamp));
    
    if (maxTimestamp > lastReadTime) {
      setHasUnreadMessages(true);
    } else {
      setHasUnreadMessages(false);
    }
  }, [profile]);

  const markChatAsRead = useCallback(async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || !profile) return;

    // Optimistic update
    setHasUnreadMessages(false);
    
    try {
      const now = Date.now() / 1000;
      const currentUserData = profile.userData || {};
      
      // Don't update if already up to date (optimization)
      const lastRead = parseFloat(currentUserData['lastChatReadTimestamp'] || '0');
      if (lastRead >= now - 5) return; // small buffer

      const newUserData = {
        ...currentUserData,
        'lastChatReadTimestamp': now.toString()
      };

      // Optimistic profile update
      setProfile(prev => prev ? ({
        ...prev,
        userData: newUserData
      }) : null);

      await PrivateProfileService.updateProfile(userId, {
        userData: newUserData
      });
      console.log('✅ Chat marked as read');
    } catch (error) {
      console.error('❌ Failed to mark chat as read:', error);
    }
  }, [profile]);

  // Load user profile when authentication state changes
  useEffect(() => {
    console.log('🔄 Setting up auth state listener for chat...');
    
    if (!onAuthStateChanged || !auth) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user: any) => {
      console.log('🔐 Auth state changed in chat context:', user ? 'User logged in' : 'User logged out');
      if (user) {
        loadUserProfile();
      } else {
        // Clear chat when user logs out
        setProfile(null);
        setMessages([]);
        console.log('🧹 Cleared chat data on logout');
      }
    });
    return unsubscribe;
  }, []);

  // Load user profile on mount (fallback)
  useEffect(() => {
    if (auth.currentUser) {
      console.log('📱 Initial profile load on mount');
      loadUserProfile();
    }
  }, []);

  const loadUserProfile = useCallback(async () => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.log('❌ No authenticated user found for chat history');
      return;
    }

    console.log('🔄 Loading user profile for chat history...');
    try {
      const result = await PrivateProfileService.getProfile(userId);
      if (result.success && result.data) {
        console.log('✅ Profile loaded successfully');
        setProfile(result.data);
        
        // Automatically load chat history when profile is loaded
        const chatHistory = result.data.chatHistory || [];
        console.log(`📚 Found ${chatHistory.length} total chat history entries`);
        
        if (chatHistory.length > 0) {
          // Limit to most recent interactions (50 messages total)
          const uiMessages = convertChatHistoryToMessages(chatHistory, CHAT_HISTORY_LIMIT);
          console.log(`💬 Loaded ${uiMessages.length} most recent messages (max ${CHAT_HISTORY_LIMIT * 2})`);
          setMessages(uiMessages);
        } else {
          console.log('📝 No chat history found - starting fresh');
          setMessages([]);
        }
      } else {
        console.log('❌ Failed to load profile:', result.error);
      }
    } catch (error) {
      console.error('❌ Failed to load user profile:', error);
    }
  }, []); // Empty deps - doesn't depend on any state

  // Convert chat history to UI messages with optional limit
  const convertChatHistoryToMessages = (chatHistory: MentorChatHistory[], maxInteractions?: number): ChatMessage[] => {
    // Sort by timestamp descending (most recent first)
    const sortedHistory = [...chatHistory].sort((a, b) => b.timestamp - a.timestamp);
    
    // Limit to most recent interactions if specified
    const limitedHistory = maxInteractions ? sortedHistory.slice(0, maxInteractions) : sortedHistory;
    
    console.log(`📝 Processing ${limitedHistory.length} chat interactions (limited from ${chatHistory.length} total)`);
    
    const uiMessages: ChatMessage[] = [];
    
    limitedHistory.forEach((chat) => {
      // Add user message
      uiMessages.push({
        id: `${chat.id}_user`,
        role: 'user',
        content: chat.userMessage,
        timestamp: chat.timestamp,
      });
      
      // Check if this chat has recommendations (two-phase pattern)
      const hasRecommendations = chat.recommendations && 
        ((chat.recommendations.books?.length || 0) > 0 || (chat.recommendations.insights?.length || 0) > 0);
      
      if (hasRecommendations) {
        // Phase 1 message (text only, with profile updates)
        uiMessages.push({
          id: `${chat.id}_assistant`,
          role: 'assistant',
          content: chat.assistantResponse,
          timestamp: chat.timestamp + 0.1,
          profileUpdates: chat.profileUpdates,
        });
        
        // Phase 2 message (recommendations) - use stored text or default
        // Note: We don't save the Phase 2 text separately, so use a default
        uiMessages.push({
          id: `${chat.id}_phase2`,
          role: 'assistant',
          content: 'Here are some resources for you:',
          timestamp: chat.timestamp + 0.2,
          recommendations: {
            books: chat.recommendations!.books || [],
            insights: chat.recommendations!.insights || [],
          },
        });
      } else {
        // Single message (no recommendations)
      uiMessages.push({
        id: `${chat.id}_assistant`,
        role: 'assistant',
        content: chat.assistantResponse,
          timestamp: chat.timestamp + 0.1,
        profileUpdates: chat.profileUpdates,
      });
      }
    });
    
    // Sort by timestamp ascending (oldest first) for proper chat display order
    return uiMessages.sort((a, b) => a.timestamp - b.timestamp);
  };

  const loadChatHistory = useCallback(async () => {
    console.log('🔄 Explicit loadChatHistory called');
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.log('❌ No authenticated user for loadChatHistory');
      return;
    }

    setIsLoading(true);
    try {
      await loadUserProfile(); // Reuse the existing logic
      // Clear any previous access-related errors when successfully loading
      setError(null);
      console.log('✅ Chat history reloaded');
    } catch (error) {
      console.error('❌ Failed to load chat history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadUserProfile]); // Proper dependency

  // Helper function to format user-friendly error messages
  const formatErrorMessage = (error: any): string => {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      
      // Check for timeout/gateway errors
      if (message.includes('504') || message.includes('gateway time-out') || message.includes('timeout')) {
        return 'The request took too long to complete. The mentor AI might be experiencing high load. Please try again.';
      }
      
      // Check for 503 service unavailable
      if (message.includes('503') || message.includes('service unavailable')) {
        return 'The mentor service is temporarily unavailable. Please try again in a few moments.';
      }
      
      // Check for 500 internal server error
      if (message.includes('500') || message.includes('internal server error')) {
        return 'An unexpected error occurred on the server. Please try again.';
      }
      
      // Check for network errors
      if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
        return 'Network connection error. Please check your internet connection and try again.';
      }
      
      // Check for authentication errors
      if (message.includes('auth') || message.includes('permission') || message.includes('unauthorized')) {
        return 'Authentication error. Please try logging out and back in.';
      }
      
      // Return the original error message if it's user-friendly
      return error.message;
    }
    
    return 'An unexpected error occurred. Please try again.';
  };

  const clearError = useCallback(() => {
    setError(null);
    setLastFailedMessage(null);
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !profile || isStreaming) return;
    
    // Check AI Premium access or free trial for mentor chat
    const userMessageCount = messages.filter(msg => msg.role === 'user').length;
    const hasFreeTrial = userMessageCount < FREE_MESSAGE_LIMIT;
    const canSendMessage = hasAIAccess || hasFreeTrial;
    
    if (!canSendMessage) {
      setError(`You've used all ${FREE_MESSAGE_LIMIT} free messages. Upgrade to AI Premium to continue chatting with your mentor.`);
      return;
    }
    
    // Clear any previous errors when user can send
    setError(null);

    const messageId = `msg_${Date.now()}`;
    const timestamp = Date.now() / 1000;

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: `${messageId}_user`,
      role: 'user',
      content: message,
      timestamp,
    };

    setMessages(prev => [...prev, userMessage]);

    // Add placeholder for assistant message (shows thinking indicator)
    const assistantPlaceholder: ChatMessage = {
      id: `${messageId}_assistant`,
      role: 'assistant',
      content: '',
      timestamp: timestamp + 0.1,
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantPlaceholder]);

    // Set loading state while we await response
    setIsStreaming(true);
    setStreamingMessageId(`${messageId}_assistant`);
    setCurrentStreamingMessage('');

    try {
      // Determine response language from device/profile
      const deriveLanguage = (): string | undefined => {
        try {
          // Web
          if (typeof navigator !== 'undefined' && (navigator as any).language) {
            return ((navigator as any).language as string).slice(0, 2);
          }
          // Fallback using Intl (RN + Web)
          const intlLocale = (Intl && Intl.DateTimeFormat().resolvedOptions().locale) || '';
          if (intlLocale) return intlLocale.slice(0, 2);
        } catch {}
        // Fallback to profile language if present (handled in service as well)
        return undefined;
      };

      // Prepare messages for AI (only user/assistant - server owns system prompts)
      const historyMessages = MentorChatService
        .convertChatHistory((profile.chatHistory || []).slice(-10))
        .map(h => ({ role: h.role as 'user' | 'assistant', content: h.message }));
      
      const userProfile = MentorChatService.createUserProfile(profile);
      const responseLanguage = deriveLanguage();
      const userAudiences = profile.audiences || [];
      const recommendationOptions = {
        responseLanguage,
        audiences: userAudiences,
        audienceFilterMode: 'strict' as const,
      };
      
      // No system messages - server handles primary.md and with_books.md prompts
      const messages = [
        ...historyMessages,
        { role: 'user' as const, content: message }
      ];

      console.log('📡 Phase 1: Getting response and checking if recommendations needed...');

      // PHASE 1: Get text response + optional search query
      // Server uses primary.md prompt
      const phase1Result = await mentorChatService.chatPhase1(
        messages,
        ChatPhase1Schema,
        recommendationOptions,
        userProfile
      );

      console.log('✅ Phase 1 complete:', {
        hasSearchQuery: !!phase1Result.searchQuery && phase1Result.searchQuery !== '',
        profileChangesCount: phase1Result.profileChanges?.length || 0
      });
      
      let completedText = phase1Result.text;
      let recommendations: any = undefined;
      
      // Process incremental profile changes from Phase 1 (compact array format)
      let profileUpdates: any = undefined;
      let hasFocusChange = false;
      let focusChangeData: any = null;
      const profileChanges = (phase1Result as any).profileChanges;
      
      // profileChanges: [{ operation: 'add|remove|set', field: 'goals|...', value: 'item' }, ...]
      if (Array.isArray(profileChanges) && profileChanges.length > 0) {
        console.log('📝 Applying profile changes:', profileChanges);
        
        try {
          // Group changes by field and operation
          const changesByField: Record<string, { add: string[], remove: string[] }> = {
            goals: { add: [], remove: [] },
            interests: { add: [], remove: [] },
            challenges: { add: [], remove: [] },
            values: { add: [], remove: [] },
            personalityTraits: { add: [], remove: [] },
          };
          
          // Handle single-value fields (mainFocusCategory, mainFocusLabel)
          const singleValueFields: Record<string, string> = {};

          profileChanges.forEach((change: any) => {
            if (change.operation === 'set') {
              // Single-value fields like mainFocusLabel
              singleValueFields[change.field] = change.value;
              
              // Track focus change for later
              if (change.field === 'mainFocusLabel') {
                focusChangeData = change.value;
                hasFocusChange = true;
              }
            } else if (change.operation === 'add' && changesByField[change.field]) {
              changesByField[change.field].add.push(change.value);
            } else if (change.operation === 'remove' && changesByField[change.field]) {
              changesByField[change.field].remove.push(change.value);
            }
          });

          // Apply array field changes to current profile
          const currentProfile = {
            goals: profile.coreProfile?.goals || [],
            interests: profile.coreProfile?.interests || [],
            challenges: profile.coreProfile?.challenges || [],
            values: (profile.coreProfile as any)?.values || [],
            personalityTraits: profile.coreProfile?.personalityTraits || [],
          };

          const applyChanges = (current: string[], add: string[], remove: string[]) => {
            const filtered = current.filter(item => !remove.includes(item));
            return [...new Set([...filtered, ...add])]; // Deduplicate
          };

          const updatedProfile: any = {};
          let hasChanges = false;

          // Process array fields
          Object.keys(changesByField).forEach((field) => {
            const { add, remove } = changesByField[field];
            if (add.length > 0 || remove.length > 0) {
              updatedProfile[field] = applyChanges(currentProfile[field as keyof typeof currentProfile], add, remove);
              hasChanges = true;
            }
          });

          // Process single-value fields
          Object.keys(singleValueFields).forEach((field) => {
            updatedProfile[field] = singleValueFields[field];
            hasChanges = true;
          });

          if (hasChanges) {
            await PrivateProfileService.updateCoreProfile(userId, updatedProfile, 'conversation:incremental');
            console.log('✅ Profile changes applied successfully');

          // Store for display (show add/remove/set format)
          const displayChanges: any = {};
          profileChanges.forEach((change: any) => {
            if (change.operation === 'set') {
              // Don't show mainFocus changes in profile updates card (handled separately)
              if (change.field !== 'mainFocusLabel') {
                if (!displayChanges[change.field]) displayChanges[change.field] = [];
                displayChanges[change.field].push(`→ ${change.value}`);
              }
            } else {
              if (!displayChanges[change.field]) displayChanges[change.field] = [];
              const prefix = change.operation === 'add' ? '+ ' : '- ';
              displayChanges[change.field].push(`${prefix}${change.value}`);
            }
          });

            if (Object.keys(displayChanges).length > 0) {
              profileUpdates = { coreProfile: displayChanges };
            }
          }
        } catch (updateError) {
          console.error('❌ Failed to apply profile changes:', updateError);
        }
      }

      // Show Phase 1 text immediately
      await new Promise(res => setTimeout(res, 500 + Math.random() * 300));
      
      // Check if Phase 2 is needed
      const searchQuery = (phase1Result as any).searchQuery;
      const needsPhase2 = searchQuery && typeof searchQuery === 'string' && searchQuery.trim() !== '';
      
      // Update Phase 1 message with content and profile updates in ONE state update
      setMessages(prev => prev.map(msg => 
        msg.id === `${messageId}_assistant` 
          ? { 
              ...msg, 
              content: completedText, 
              isStreaming: needsPhase2,  // Keep streaming if Phase 2 coming
              profileUpdates: profileUpdates  // Add profile updates here
            }
          : msg
      ));

      // PHASE 2: Get recommendations if searchQuery is not empty
      if (needsPhase2) {
        console.log('📡 Phase 2: Getting recommendations with search query:', searchQuery);
        
        // Stop streaming on Phase 1 message immediately
        setMessages(prev => prev.map(msg => 
          msg.id === `${messageId}_assistant` 
            ? { ...msg, isStreaming: false }
            : msg
        ));
        
        // Add Phase 2 placeholder message (shows thinking indicator)
        const phase2MessageId = `${messageId}_phase2`;
        const phase2Message: ChatMessage = {
          id: phase2MessageId,
          role: 'assistant',
          content: '',
          timestamp: timestamp + 0.2,
          isStreaming: true,
        };
        setMessages(prev => [...prev, phase2Message]);
        
        try {
          // Server uses with_books.md prompt for Phase 2
          const phase2Result = await mentorChatService.chatPhase2(
            messages,
            searchQuery,
            ChatPhase2Schema,
            recommendationOptions,
            userProfile
          );

          console.log('✅ Phase 2 complete:', {
            booksCount: phase2Result.books?.length || 0,
            insightsCount: phase2Result.insights?.length || 0
          });

          recommendations = {
            books: phase2Result.books || [],
            insights: phase2Result.insights || []
          };
          
          const phase2Text = phase2Result.text && phase2Result.text.trim() 
            ? phase2Result.text 
            : 'Here are some resources for you:';
          
          // Format recommendations for Phase 2 message
          const formattedRecsPhase2 = {
            books: (recommendations.books || []).map((book: any) => ({ 
              bookId: String(book.bookId),
              reason: book.reason || '', // Backend may not return reason
              recommendedAt: timestamp 
            })),
            insights: (recommendations.insights || []).map((insight: any) => ({ 
              insightId: String(insight.insightId),
              reason: insight.reason || '', // Backend may not return reason
              bookId: insight.bookId ? String(insight.bookId) : undefined,
              recommendedAt: timestamp 
            }))
          };
          
          // Show Phase 2 message with animation (all data in one update to prevent jumping)
          await new Promise(res => setTimeout(res, 500 + Math.random() * 300));
          
          setMessages(prev => prev.map(msg => 
            msg.id === phase2MessageId
              ? { 
                  ...msg, 
                  content: phase2Text, 
                  isStreaming: false, 
                  recommendations: formattedRecsPhase2 
                }
              : msg
          ));
          
          // Keep raw recommendations for saving to Firebase (will be converted to strings in save logic)
          recommendations = {
            books: phase2Result.books || [],
            insights: phase2Result.insights || []
          };
          
        } catch (phase2Error) {
          console.error('❌ Phase 2 failed (non-fatal):', phase2Error);
          // Stop streaming on Phase 1 message
          setMessages(prev => prev.map(msg => 
            msg.id === `${messageId}_assistant` 
              ? { ...msg, isStreaming: false }
              : msg
          ));
        }
      } else {
        console.log('ℹ️ No searchQuery - skipping Phase 2 (no recommendations needed)');
        // No Phase 2, just mark Phase 1 as complete (already has profileUpdates from earlier)
        setMessages(prev => prev.map(msg => 
          msg.id === `${messageId}_assistant` 
            ? { ...msg, isStreaming: false }
            : msg
        ));
      }
      
      setIsStreaming(false);
      setStreamingMessageId(null);
      setCurrentStreamingMessage('');

      // Note: Focus change is now handled in profileChanges above via updateCoreProfile
      // No separate updateMainFocus call needed
      if (hasFocusChange && focusChangeData) {
        console.log('🎯 Focus change detected:', focusChangeData);
      }

      // Save chat interaction to Firebase after animation completes
      try {
        const formattedRecommendationsForSave = recommendations ? {
          books: (recommendations.books || []).map((book: any) => ({ 
            bookId: String(book.bookId),
            reason: book.reason || '', // Backend may not return reason
            recommendedAt: timestamp 
          })),
          insights: (recommendations.insights || []).map((insight: any) => ({ 
            insightId: String(insight.insightId),
            reason: insight.reason || '', // Backend may not return reason
            bookId: insight.bookId ? String(insight.bookId) : undefined,
            recommendedAt: timestamp 
          }))
        } : undefined;
        
        const chatEntry: MentorChatHistory = {
          id: messageId,
          timestamp,
          userMessage: message,
          assistantResponse: completedText,
          recommendations: formattedRecommendationsForSave,
          profileUpdates: profileUpdates,
        };
        await PrivateProfileService.addUserChatHistory(userId, chatEntry);

        // Don't reload profile here - causes re-renders and jumping
        // Profile updates were already applied immediately from Phase 1
        
        // Log chat message received
        await loggingService.log(
          chatMessageReceived(
            messageId,
            'general_mentor', // Hardcoded on server
            !!formattedRecommendationsForSave
          )
        );
      } catch (saveError) {
        console.error('❌ Failed to save chat interaction:', saveError);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Format user-friendly error message
      const errorMessage = formatErrorMessage(error);
      
      // Store the failed message for retry
      setLastFailedMessage(message);
      setError(errorMessage);
      
      // Remove the placeholder assistant message since we failed
      setMessages(prev => prev.filter(msg => msg.id !== `${messageId}_assistant`));
      
      // Clear streaming state
      setIsStreaming(false);
      setStreamingMessageId(null);
      setCurrentStreamingMessage('');
    }
  }, [profile, isStreaming, loadUserProfile]);

  const retryLastMessage = useCallback(async () => {
    if (!lastFailedMessage) return;
    
    clearError();
    await sendMessage(lastFailedMessage);
  }, [lastFailedMessage, clearError, sendMessage]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setCurrentStreamingMessage('');
    setStreamingMessageId(null);
    setIsStreaming(false);
    setError(null);
    setLastFailedMessage(null);
  }, []);

  const handleQuickAction = useCallback((userMessage: string, mentorResponse: string) => {
    const userId = auth.currentUser?.uid;
    if (isStreaming || !userId || !profile) return; // Don't add quick actions while streaming or without auth

    // Prevent duplicate quick actions - check if the same message was sent recently (within 10 seconds)
    const recentMessages = messages.slice(-4); // Check last 4 messages (2 interactions)
    const isDuplicate = recentMessages.some(msg => 
      msg.role === 'user' && 
      msg.content === userMessage && 
      (Date.now() / 1000 - msg.timestamp) < 10
    );
    
    if (isDuplicate) {
      return;
    }

    const messageId = `quickaction_${Date.now()}`;
    const timestamp = Date.now() / 1000;

    // Add user message immediately
    const userMsg: ChatMessage = {
      id: `${messageId}_user`,
      role: 'user',
      content: userMessage,
      timestamp,
    };

    setMessages(prev => [...prev, userMsg]);

    // Show thinking indicator
    setIsStreaming(true);
    setStreamingMessageId(`${messageId}_assistant`);
    setCurrentStreamingMessage('');

    // Add placeholder for thinking indicator
    const streamingMessage: ChatMessage = {
      id: `${messageId}_assistant`,
      role: 'assistant',
      content: '',
      timestamp: timestamp + 0.1,
      isStreaming: true,
    };
    setMessages(prev => [...prev, streamingMessage]);

    // Show thinking indicator briefly, then display full message
    const displayMessage = async () => {
      try {
        // Show thinking indicator
        await new Promise(resolve => setTimeout(resolve, 400 + Math.random() * 300));
        
        // Display full message (ChatBubble animation will handle the appearance)
        setMessages(prev => prev.map(msg => 
          msg.id === `${messageId}_assistant` 
            ? { ...msg, content: mentorResponse, isStreaming: false }
            : msg
        ));
        
        setIsStreaming(false);
        setStreamingMessageId(null);
        setCurrentStreamingMessage('');
        
        // Save quick action conversation to Firebase for AI context
        const chatEntry: MentorChatHistory = {
          id: messageId,
          timestamp,
          userMessage: userMessage,
          assistantResponse: mentorResponse,
          // No recommendations for quick action responses
        };
        
        await PrivateProfileService.addUserChatHistory(userId, chatEntry);
        
        // Don't reload profile here - causes re-renders and jumping
      } catch (error) {
        console.error('❌ Error in quick action:', error);
        // Fallback to immediate display
        setIsStreaming(false);
        setStreamingMessageId(null);
        setCurrentStreamingMessage('');
        setMessages(prev => prev.map(msg => 
          msg.id === `${messageId}_assistant` 
            ? { ...msg, content: mentorResponse, isStreaming: false }
            : msg
        ));
        
        // Still try to save to Firebase
        try {
          const chatEntry: MentorChatHistory = {
            id: messageId,
            timestamp,
            userMessage: userMessage,
            assistantResponse: mentorResponse,
          };
          
          await PrivateProfileService.addUserChatHistory(userId, chatEntry);
          // Don't reload profile here - causes re-renders and jumping
        } catch (saveError) {
          console.error('❌ Failed to save quick action conversation (fallback):', saveError);
        }
      }
    };

    // Start the display process
    displayMessage();


  }, [isStreaming, profile, loadUserProfile]);

  const value: MentorChatContextType = {
    messages,
    isLoading,
    isStreaming,
    profile,
    sendMessage,
    loadChatHistory,
    clearChat,
    handleQuickAction,
    currentStreamingMessage,
    streamingMessageId,
    error,
    lastFailedMessage,
    retryLastMessage,
    clearError,
    hasUnreadMessages,
    markChatAsRead,
  };

  return (
    <MentorChatContext.Provider value={value}>
      {children}
    </MentorChatContext.Provider>
  );
}; 